import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useCarrinho } from '../services/CarrinhoContext';
import { formatPrice } from '../utils/currency';
import { useLocation, useParams } from 'react-router-dom';
import { ShareAndroid } from 'iconoir-react';
import ProductSEOMetaTags from './ProductSEOMetaTags';
import LojaFechadaAlert, { checkLojaAberta } from './LojaFechadaAlert';
import './ModalMobile.css';
import './ProdutoModal.css';

const imageAnimationStyle = `
  @keyframes smoothZoomPan {
    0% {
      transform: scale(1) translate(0, 0);
    }
    25% {
      transform: scale(1.05) translate(1%, -1%);
    }
    50% {
      transform: scale(1.08) translate(-1%, 1%);
    }
    75% {
      transform: scale(1.05) translate(1%, 0);
    }
    100% {
      transform: scale(1) translate(0, 0);
    }
  }
`;

function ProdutoModal({ produto, show, onHide }) {
  const { adicionarItem, mesa, tipoEntrega } = useCarrinho();
  const location = useLocation();
  const params = useParams();
  const brand = window.location.hostname.split('.')[0];
  const loja = window.lojaInfo || null;
  
  const [gruposSelecoes, setGruposSelecoes] = useState({});
  const [gruposQuantidades, setGruposQuantidades] = useState({});
  const [variacaoSelecionada, setVariacaoSelecionada] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const modalBodyRef = useRef(null);
  const [shakeItem, setShakeItem] = useState(null);
  const [shakeGroup, setShakeGroup] = useState(null);
  const [lojaAberta, setLojaAberta] = useState(true);
  const [adicionando, setAdicionando] = useState(false);
  const [comentario, setComentario] = useState('');
  
  // Verificar se deve mostrar apenas o cardápio (sem interação)
  const isDeliveryRoute = location.pathname.includes('/delivery');
  const isBalcaoRoute = location.pathname.includes('/balcao');
  const isMesaRoute = location.pathname.includes('/dinein/') || location.pathname.includes('/tablet/');
  const temMesa = mesa !== null;
  const modoVisualizacao = !temMesa && !isDeliveryRoute && !isBalcaoRoute;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if ((isDeliveryRoute || isBalcaoRoute || isMesaRoute) && window.lojaInfo) {
      const checkStatus = () => {
        setLojaAberta(checkLojaAberta(window.lojaInfo));
      };
      checkStatus();
      const interval = setInterval(checkStatus, 60000);
      return () => clearInterval(interval);
    }
  }, [isDeliveryRoute, isBalcaoRoute, isMesaRoute]);

  const selecionarOpcional = (grupoId, opcionalId) => {
    setGruposSelecoes(prev => {
      const grupo = produto.gruposAdicionais?.find(g => g.id === grupoId);
      if (!grupo) return prev;
      if (!(grupo.max === 1)) return prev;

      const selecaoAtual = prev[grupoId] || [];
      const jaEstaSeleccionado = selecaoAtual.includes(opcionalId);
      if (jaEstaSeleccionado) {
        return {
          ...prev,
          [grupoId]: selecaoAtual.filter(id => id !== opcionalId)
        };
      } else {
        let novaSelecao = [...selecaoAtual, opcionalId];
        if (grupo.max && novaSelecao.length > grupo.max) {
          novaSelecao = novaSelecao.slice(-grupo.max);
        }
        return {
          ...prev,
          [grupoId]: novaSelecao
        };
      }
    });
  };

  const getGrupoById = (grupoId) => produto.gruposAdicionais?.find(g => g.id === grupoId);

  const getGrupoTotal = (grupoId) => {
    const grupo = getGrupoById(grupoId);
    if (!grupo) return 0;
    if (grupo.max === 1) {
      return (gruposSelecoes[grupoId] || []).length;
    }
    const mapa = gruposQuantidades[grupoId] || {};
    return Object.values(mapa).reduce((acc, v) => acc + (v || 0), 0);
  };

  const getQuantidadeSelecionada = (grupoId) => {
    return getGrupoTotal(grupoId);
  };

  const isOpcionalSelecionado = (grupoId, opcionalId) => {
    const grupo = getGrupoById(grupoId);
    if (!grupo) return false;
    if (grupo.max === 1) {
      return (gruposSelecoes[grupoId] || []).includes(opcionalId);
    }
    const mapa = gruposQuantidades[grupoId] || {};
    return (mapa[opcionalId] || 0) > 0;
  };

  const incrementarOpcional = (grupo, opcionalId) => {
    const total = getGrupoTotal(grupo.id);
    if (grupo.max && total >= grupo.max) {
      setShakeItem({ groupId: grupo.id, opcionalId });
      setShakeGroup(grupo.id);
      setTimeout(() => {
        setShakeItem(null);
        setShakeGroup(null);
      }, 600);
      return;
    }
    setGruposQuantidades(prev => {
      const mapa = { ...(prev[grupo.id] || {}) };
      mapa[opcionalId] = (mapa[opcionalId] || 0) + 1;
      return { ...prev, [grupo.id]: mapa };
    });
  };

  const decrementarOpcional = (grupoId, opcionalId) => {
    setGruposQuantidades(prev => {
      const mapa = { ...(prev[grupoId] || {}) };
      mapa[opcionalId] = Math.max(0, (mapa[opcionalId] || 0) - 1);
      return { ...prev, [grupoId]: mapa };
    });
  };

  const grupoTemItensAtivos = (grupo) => {
    if (!grupo.grupoOpcionais || grupo.grupoOpcionais.length === 0) return false;
    return grupo.grupoOpcionais.some(opcional => 
      opcional.b_ativo === 1 || opcional.b_ativo === '1' || opcional.b_ativo === true
    );
  };

  const validarSelecoes = () => {
    // Validar variação obrigatória
    if (produto.variacoes && produto.variacoes.length > 0 && !variacaoSelecionada) {
      return false;
    }
    
    if (!produto.gruposAdicionais) return true;
    
    for (const grupo of produto.gruposAdicionais) {
      // Se o grupo é obrigatório mas não tem itens ativos, ignorar validação
      if (grupo.b_obrigatorio && !grupoTemItensAtivos(grupo)) {
        continue;
      }
      
      const quantidadeSelecionada = getGrupoTotal(grupo.id);
      const minimo = grupo.min || 0;
      
      // Só é obrigatório se b_obrigatorio = 1 (ou true) E tem itens ativos
      if (grupo.b_obrigatorio && grupoTemItensAtivos(grupo) && quantidadeSelecionada < minimo) {
        return false;
      }
    }
    
    return true;
  };

  const getValorProduto = () => {
    if (variacaoSelecionada) {
      return parseFloat(variacaoSelecionada.valor);
    }
    // Usar valor com desconto se disponível, senão usar valor original
    const valorComDesconto = produto.em_promocao && produto.promocao_info?.valor_promocional 
      ? produto.promocao_info.valor_promocional 
      : produto.valor;
    return parseFloat(valorComDesconto);
  };

  const calcularValorTotal = () => {
    let total = getValorProduto();

    // Calcular valor dos grupos de opcionais
    if (produto.gruposAdicionais) {
      produto.gruposAdicionais.forEach(grupo => {
        // Só somar valor se b_adicional = 1 (grupo de adicionais)
        if (grupo.b_adicional === 1) {
          if (grupo.max === 1) {
            const selecoes = gruposSelecoes[grupo.id] || [];
            selecoes.forEach(opcionalId => {
              const opcional = grupo.grupoOpcionais?.find(o => o.id === opcionalId);
              if (opcional) {
                total += parseFloat(opcional.valor || 0);
              }
            });
          } else {
            const mapa = gruposQuantidades[grupo.id] || {};
            Object.entries(mapa).forEach(([opcionalId, quantidade]) => {
              if (quantidade > 0) {
                const opcional = grupo.grupoOpcionais?.find(o => o.id === parseInt(opcionalId));
                if (opcional) {
                  total += parseFloat(opcional.valor || 0) * quantidade;
                }
              }
            });
          }
        }
      });
    }

    return total;
  };

  const adicionarAoCarrinho = async () => {
    if (!validarSelecoes()) {
      let mensagem = 'Por favor, complete as seleções obrigatórias:';
      
      if (produto.variacoes && produto.variacoes.length > 0 && !variacaoSelecionada) {
        mensagem += '\n- Escolha uma variação';
      }
      
      if (produto.gruposAdicionais) {
        produto.gruposAdicionais.forEach(grupo => {
          const quantidadeSelecionada = getGrupoTotal(grupo.id);
          const minimo = grupo.min || 0;
          
          // Só mostrar erro se b_obrigatorio = 1 (ou true)
          if (grupo.b_obrigatorio && quantidadeSelecionada < minimo) {
            mensagem += `\n- ${grupo.nome}: selecione pelo menos ${minimo} ${minimo === 1 ? 'opção' : 'opções'}`;
          }
        });
      }
      
      alert(mensagem);
      return;
    }

    setAdicionando(true);

    const adicionaisSelecionados = [];
    const variacoesSelecionadas = variacaoSelecionada ? [variacaoSelecionada] : [];

    // Grupos de opcionais
    if (produto.gruposAdicionais) {
      produto.gruposAdicionais.forEach(grupo => {
        if (grupo.max === 1) {
          const selecoes = gruposSelecoes[grupo.id] || [];
          selecoes.forEach(opcionalId => {
            const opcional = grupo.grupoOpcionais?.find(o => o.id === opcionalId);
            if (opcional) {
              adicionaisSelecionados.push({
                ...opcional,
                b_adicional: grupo.b_adicional // Incluir info do grupo
              });
            }
          });
        } else {
          const mapa = gruposQuantidades[grupo.id] || {};
          Object.entries(mapa).forEach(([opcionalId, quantidade]) => {
            if (quantidade > 0) {
              const opcional = grupo.grupoOpcionais?.find(o => o.id === parseInt(opcionalId));
              if (opcional) {
                for (let i = 0; i < quantidade; i++) {
                  adicionaisSelecionados.push({
                    ...opcional,
                    b_adicional: grupo.b_adicional // Incluir info do grupo
                  });
                }
              }
            }
          });
        }
      });
    }

    adicionarItem(produto, variacoesSelecionadas, adicionaisSelecionados, [], comentario);
    
    await new Promise(resolve => setTimeout(resolve, 600));
    
    setGruposSelecoes({});
    setGruposQuantidades({});
    setVariacaoSelecionada(null);
    setComentario('');
    setAdicionando(false);
    onHide();
  };

  const handleShare = async () => {
    const basePath = location.pathname.includes('/delivery') ? '/delivery' : location.pathname.includes('/balcao') ? '/balcao' : '';
    const url = `${window.location.origin}${basePath}/produto/${produto.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: produto.nome,
          text: `Confira ${produto.nome} - ${formatPrice(getValorProduto())}`,
          url: url
        });
      } catch (error) {
        // Usuário cancelou
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copiado!');
      } catch (error) {
        alert(`Link: ${url}`);
      }
    }
  };



  return (
    <>
      <ProductSEOMetaTags produto={produto} brand={brand} loja={loja} />
      <style>{imageAnimationStyle}</style>
      <Modal 
        show={show} 
        onHide={onHide} 
        size={isMobile ? undefined : "lg"} 
        centered
        className={!isMobile ? 'produto-modal-desktop' : ''}
        dialogClassName={isMobile ? 'modal-dialog-centered' : ''}
        style={isMobile ? { zIndex: 1060 } : { zIndex: 1060 }}
      >
      {isMobile ? (
        <>
          <Modal.Body ref={modalBodyRef} style={{ 
            padding: 0,
            overflowY: 'auto',
            maxHeight: '100vh',
            paddingBottom: modoVisualizacao ? '80px' : '100px'
          }}>
            <div style={{ position: 'relative', height: '350px', overflow: 'hidden', isolation: 'isolate' }}>
              <img 
                src={produto.imagem ? `https://grubupload.s3.amazonaws.com/${produto.imagem}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
                alt={produto.nome}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  animation: 'smoothZoomPan 20s ease-in-out infinite',
                  position: 'relative',
                  zIndex: 1
                }}
                onError={(e) => {
                  e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg';
                }}
              />
              <div className="image-title-overlay" style={{ zIndex: 10 }}>
                <h3>{produto.nome}</h3>
              </div>
              <Button 
                variant="light" 
                onClick={onHide}
                className="modal-close-btn"
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  zIndex: 20,
                  backgroundColor: 'rgb(53 53 53 / 74%)',
                  border: 'none',
                  color: 'rgb(232 232 232)',
                  boxShadow: 'rgba(0, 0, 0, 0.15) 0px 2px 8px'
                }}
              >
                ×
              </Button>
              <Button 
                variant="light" 
                onClick={handleShare}
                className="modal-share-btn"
                style={{
                  position: 'absolute',
                  top: '12px',
                  left: '12px',
                  zIndex: 20,
                  backgroundColor: 'rgb(53 53 53 / 74%)',
                  border: 'none',
                  color: 'rgb(232 232 232)',
                  boxShadow: 'rgba(0, 0, 0, 0.15) 0px 2px 8px'
                }}
              >
                <ShareAndroid width={22} height={22} />
              </Button>
            </div>
            
            <div style={{ padding: '20px', paddingBottom: modoVisualizacao ? '20px' : '100px' }}>
              {(isDeliveryRoute || isBalcaoRoute || isMesaRoute) && window.lojaInfo && (
                <LojaFechadaAlert loja={window.lojaInfo} />
              )}
              {((produto.em_promocao === true) || (produto.promocao_info?.b_promocao === 1) || (produto.promocao_info?.b_promocao === '1') || (produto.b_promocao === 1) || (produto.b_promocao === '1') || (produto.b_promocao === true)) && (
                <div style={{
                  width:'100%',
                  background:'#dc2626',
                  color:'#ffffff',
                  textAlign:'center',
                  padding:'6px 0',
                  fontSize:'0.85rem',
                  fontWeight:700,
                  borderRadius:'8px',
                  marginBottom:'12px'
                }}>EM PROMOÇÃO</div>
              )}
              <p style={{ 
                color: '#666', 
                marginBottom: '16px',
                fontSize: '0.9rem'
              }}>
                {produto.descricao}
              </p>
              {(() => {
                const original = (produto.valor_original ?? produto.valor);
                const isPromo = (produto.em_promocao === true) || (produto.promocao_info?.b_promocao === 1) || (produto.promocao_info?.b_promocao === '1') || (produto.b_promocao === 1) || (produto.b_promocao === '1') || (produto.b_promocao === true);
                if (isPromo) {
                  return (
                    <div style={{color:'#6b7280', fontWeight:400, fontSize:'0.85rem', textDecoration:'line-through', marginBottom:'4px'}}>
                      {formatPrice(original)}
                    </div>
                  );
                }
                return null;
              })()}
              <h4 style={{ 
                color: '#10b981', 
                fontWeight: '600', 
                marginBottom: '8px',
                fontSize: '16px'
              }}>
                {(() => {
                  const promo = (produto.promocao_info?.valor_promocional ?? produto.valor);
                  const isPromo = (produto.em_promocao === true) || (produto.promocao_info?.b_promocao === 1) || (produto.promocao_info?.b_promocao === '1') || (produto.b_promocao === 1) || (produto.b_promocao === '1') || (produto.b_promocao === true);
                  const valor = isPromo ? formatPrice(promo) : formatPrice(getValorProduto());
                  return produto.variacao ? `a partir de ${valor}` : valor;
                })()}
              </h4>

        {/* Variações */}
        {produto.variacoes && produto.variacoes.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h5 style={{ 
              fontWeight: '600', 
              marginBottom: '14px',
              fontSize: isMobile ? '14px' : '16px',
              color: '#1a1a1a'
            }}>
              Escolha uma variação:
            </h5>
            <div style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
              {produto.variacoes.map((variacao, idx) => (
                <div 
                  key={`variacao_${variacao.id}`}
                  onClick={() => !modoVisualizacao && setVariacaoSelecionada(variacao)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: isMobile ? '14px' : '16px',
                    borderBottom: idx !== produto.variacoes.length-1 ? '1px solid #e5e7eb' : 'none',
                    cursor: !modoVisualizacao ? 'pointer' : 'default',
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    flex: 1
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: variacaoSelecionada?.id === variacao.id ? '#006affff' : '#e5e7eb',
                      marginRight: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: variacaoSelecionada?.id === variacao.id ? '#006affff' : 'white'
                    }}>
                      {variacaoSelecionada?.id === variacao.id && (
                        <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                      )}
                    </div>
                    <div>
                      <div style={{ 
                        fontWeight: '600',
                        fontSize: isMobile ? '14px' : '14px',
                        color: '#111827',
                        marginBottom: '2px'
                      }}>
                        {variacao.nome}
                      </div>
                      <div style={{ 
                        color: '#10b981', 
                        fontWeight: '600',
                        fontSize: isMobile ? '14px' : '14px'
                      }}>
                        {formatPrice(variacao.valor)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grupos de Opcionais */}
        {produto.gruposAdicionais && produto.gruposAdicionais.length > 0 && produto.gruposAdicionais.filter(grupo => grupoTemItensAtivos(grupo)).map((grupo) => (
          <div key={`grupo_${grupo.id}`} style={{ marginBottom: '24px' }} className={shakeGroup === grupo.id ? 'shake' : ''}>
            <div style={{
              background: 'rgb(245, 246, 248)',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '0px',
              padding: '22px',
              marginBottom: '12px',
              lineHeight: '11px'
            }}>
              <h5 style={{ 
                fontWeight: '600', 
                marginBottom: '6px',
                fontSize: isMobile ? '14px' : '16px',
                color: '#1a1a1a'
              }}>
                {grupo.nome}{grupo.b_obrigatorio ? ' (Obrigatório)' : ''}
              </h5>
              {grupo.descricao && (
                <p style={{ 
                  color: '#666', 
                  fontSize: isMobile ? '13px' : '15px', 
                  marginBottom: '8px' 
                }}>
                  {grupo.descricao}
                </p>
              )}
              {(grupo.min > 0 || grupo.max) && (
                <p style={{ 
                  color: '#888', 
                  fontSize: isMobile ? '12px' : '13px', 
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  {grupo.min > 0 && `Mín: ${grupo.min} `}
                  {grupo.max && `Máx: ${grupo.max}`}
                  {!grupo.max && grupo.min > 0 && 'Máx: Ilimitado'}
                </p>
              )}
            <div style={{
              fontSize: isMobile ? '12px' : '13px',
              color: '#28a745',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {getGrupoTotal(grupo.id)}/{grupo.max || '∞'} {grupo.max === 1 ? '✓' : ''}
              {(grupo.max === 1 && grupo.b_obrigatorio && getGrupoTotal(grupo.id) >= (grupo.min || 1)) && (
                <span className="success-badge">Obrigatório selecionado ✓</span>
              )}
                {(grupo.max && grupo.max !== 1 && getGrupoTotal(grupo.id) >= grupo.max) && (
                  <span className="limit-badge" aria-label="Máximo atingido">✓</span>
                )}
            </div>
            </div>
            
            <div style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
            {grupo.grupoOpcionais && grupo.grupoOpcionais.map((opcional, idx) => {
              const opcionalDesabilitado = opcional.b_ativo === 0 || opcional.b_ativo === '0' || opcional.b_ativo === false;
              return (
              <div 
                key={`opcional_${grupo.id}_${opcional.id}`} 
                onClick={() => !modoVisualizacao && !opcionalDesabilitado && grupo.max === 1 && selecionarOpcional(grupo.id, opcional.id)}
                className={(shakeItem && shakeItem.groupId === grupo.id && shakeItem.opcionalId === opcional.id) ? 'shake' : ''}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: isMobile ? '12px' : '16px',
                  borderBottom: idx !== grupo.grupoOpcionais.length-1 ? '1px solid #e5e7eb' : 'none',
                  cursor: !modoVisualizacao && !opcionalDesabilitado ? (grupo.max === 1 ? 'pointer' : 'default') : 'default',
                  opacity: opcionalDesabilitado ? 0.5 : 1,
                  backgroundColor: opcionalDesabilitado ? '#f9fafb' : 'transparent'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flex: 1
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: grupo.max === 1 ? '50%' : '4px',
                    border: '2px solid #e5e7eb',
                    marginRight: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: opcionalDesabilitado ? '#e5e7eb' : (isOpcionalSelecionado(grupo.id, opcional.id) ? '#006affff' : 'white'),
                    borderColor: opcionalDesabilitado ? '#e5e7eb' : (isOpcionalSelecionado(grupo.id, opcional.id) ? '#006affff' : '#e5e7eb'),
                    pointerEvents: 'none',
                    flexShrink: 0
                  }}>
                    {!opcionalDesabilitado && isOpcionalSelecionado(grupo.id, opcional.id) && (
                      <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                    )}
                  </div>
                  
                  <div
                    style={{
                      width: isMobile ? '40px' : '48px',
                      height: isMobile ? '40px' : '48px',
                      marginRight: '12px',
                      flexShrink: 0,
                      backgroundImage: opcional.imagem 
                        ? `url(https://grubupload.s3.amazonaws.com/${opcional.imagem})` 
                        : 'url(https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center center',
                      borderRadius: '8px',
                      filter: opcionalDesabilitado ? 'grayscale(100%)' : 'none',
                      pointerEvents: 'none'
                    }}
                  />
                  
                  <div style={{ pointerEvents: 'none' }}>
                    <div style={{ 
                      fontWeight: '500',
                      fontSize: '0.85rem',
                      color: opcionalDesabilitado ? '#383838' : 'rgb(17, 24, 39)',
                      marginBottom: '2px',
                    }}>
                      {opcional.nome}
                    </div>
                    <div style={{ 
                      color: opcionalDesabilitado ? '#9ca3af' : ((grupo.b_adicional === 1 && opcional.valor > 0) ? 'rgb(56, 170, 78)' : '#6b7280'), 
                      fontWeight: 700,
                      fontSize: '0.8rem'
                    }}>
                      {grupo.b_adicional === 1 ? (opcional.valor > 0 ? `+ ${formatPrice(opcional.valor)}` : 'Item sem custo extra') : 'Incluído no valor'}
                    </div>
                  </div>
                </div>
                {(!modoVisualizacao && !opcionalDesabilitado && !(grupo.max === 1)) && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '20px',
                    backgroundColor: '#fff'
                  }}>
                    <Button 
                      variant="light"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); decrementarOpcional(grupo.id, opcional.id); }}
                      style={{ 
                        border: 'none', 
                        borderRadius: '50%', 
                        width: '30px', 
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'transparent',
                        color: '#666'
                      }}
                    >
                      −
                    </Button>
                    <span style={{ 
                      margin: '0 12px', 
                      minWidth: '20px', 
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      {(gruposQuantidades[grupo.id]?.[opcional.id] || 0)}
                    </span>
                    <Button 
                      variant="light"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); incrementarOpcional(grupo, opcional.id); }}
                      style={{ 
                        border: 'none', 
                        borderRadius: '50%', 
                        width: '30px', 
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'transparent',
                        color: '#666'
                      }}
                    >
                      +
                    </Button>
                  </div>
                )}
              </div>
            )})}
            </div>
          </div>
        ))}

              {/* Campo de Comentário */}
              {!modoVisualizacao && (
                <div style={{ marginTop: '24px' }}>
                  <label style={{ 
                    fontWeight: '600', 
                    marginBottom: '8px',
                    fontSize: isMobile ? '14px' : '16px',
                    color: '#1a1a1a',
                    display: 'block'
                  }}>
                    Alguma observação?
                  </label>
                  <textarea
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    placeholder="Ex: Sem cebola, ponto da carne, etc."
                    maxLength={200}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      minHeight: '80px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#9ca3af', 
                    marginTop: '4px',
                    textAlign: 'right'
                  }}>
                    {comentario.length}/200
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
        </>
      ) : (
        <Modal.Body className="p-0" style={{ padding: 0, overflowY: 'auto' }}>
          <div className="pos-product">
            <Button 
              variant="light" 
              onClick={onHide}
              className="modal-close-btn"
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                zIndex: 1000,
                backgroundColor: 'rgb(53 53 53 / 74%)',
                border: 'none',
                color: 'rgb(232 232 232)'
              }}
            >
              ×
            </Button>
            <Button 
              variant="light" 
              onClick={handleShare}
              className="modal-share-btn"
              style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                zIndex: 1000,
                backgroundColor: 'rgb(53 53 53 / 74%)',
                border: 'none',
                color: 'rgb(232 232 232)'
              }}
            >
              <ShareAndroid width={22} height={22} />
            </Button>
            <div className="pos-product-img">
              <div 
                className="img"
                style={{
                  backgroundImage: `url(${produto.imagem ? `https://grubupload.s3.amazonaws.com/${produto.imagem}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'})`
                }}
              />
              <div className="image-title-overlay">
                <h3>{produto.nome}</h3>
              </div>
            </div>
            {((produto.em_promocao === true) || (produto.promocao_info?.b_promocao === 1) || (produto.promocao_info?.b_promocao === '1') || (produto.b_promocao === 1) || (produto.b_promocao === '1') || (produto.b_promocao === true)) && (
              <div style={{
                width:'100%',
                background:'#dc2626',
                color:'#ffffff',
                textAlign:'center',
                padding:'8px 0',
                fontSize:'0.9rem',
                fontWeight:700
              }}>EM PROMOÇÃO</div>
            )}
            <div className="pos-product-info" style={{padding: '20px 24px'}}>
              {(isDeliveryRoute || isBalcaoRoute || isMesaRoute) && window.lojaInfo && (
                <LojaFechadaAlert loja={window.lojaInfo} />
              )}
              {/* Título sobre a imagem no desktop; remover duplicação aqui */}
              <p style={{ 
                color: '#666', 
                marginBottom: '16px',
                fontSize: '0.9rem'
              }}>
                {produto.descricao}
              </p>
              {(() => {
                const original = (produto.valor_original ?? produto.valor);
                const isPromo = (produto.em_promocao === true) || (produto.promocao_info?.b_promocao === 1) || (produto.promocao_info?.b_promocao === '1') || (produto.b_promocao === 1) || (produto.b_promocao === '1') || (produto.b_promocao === true);
                if (isPromo) {
                  return (
                    <div style={{color:'#6b7280', fontWeight:400, fontSize:'0.9rem', textDecoration:'line-through', marginBottom:'4px'}}>
                      {formatPrice(original)}
                    </div>
                  );
                }
                return null;
              })()}
              <h4 style={{ 
                color: '#10b981', 
                fontWeight: '600', 
                marginBottom: '8px',
                fontSize: '18px'
              }}>
                {(() => {
                  const promo = (produto.promocao_info?.valor_promocional ?? produto.valor);
                  const isPromo = (produto.em_promocao === true) || (produto.promocao_info?.b_promocao === 1) || (produto.promocao_info?.b_promocao === '1') || (produto.b_promocao === 1) || (produto.b_promocao === '1') || (produto.b_promocao === true);
                  const valor = isPromo ? formatPrice(promo) : formatPrice(getValorProduto());
                  return produto.variacao ? `a partir de ${valor}` : valor;
                })()}
              </h4>
              {produto.variacoes && produto.variacoes.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h5 style={{ 
                    fontWeight: '600', 
                    marginBottom: '14px',
                    fontSize: '16px',
                    color: '#1a1a1a'
                  }}>
                    Escolha uma variação:
                  </h5>
                  <div style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
                    {produto.variacoes.map((variacao, idx) => (
                      <div 
                        key={`variacao_${variacao.id}`}
                        onClick={() => !modoVisualizacao && setVariacaoSelecionada(variacao)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '16px',
                          borderBottom: idx !== produto.variacoes.length-1 ? '1px solid #e5e7eb' : 'none',
                          cursor: !modoVisualizacao ? 'pointer' : 'default'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          flex: 1
                        }}>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: variacaoSelecionada?.id === variacao.id ? '#006affff' : '#e5e7eb',
                            marginRight: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: variacaoSelecionada?.id === variacao.id ? '#006affff' : 'white'
                          }}>
                            {variacaoSelecionada?.id === variacao.id && (
                              <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                            )}
                          </div>
                          <div>
                            <div style={{ 
                              fontWeight: '600',
                              fontSize: '14px',
                              color: '#111827',
                              marginBottom: '2px'
                            }}>
                              {variacao.nome}
                            </div>
                            <div style={{ 
                              color: 'rgb(56, 170, 78)', 
                              fontWeight: 700,
                              fontSize: '0.8rem'
                            }}>
                              {formatPrice(variacao.valor)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {produto.gruposAdicionais && produto.gruposAdicionais.length > 0 && produto.gruposAdicionais.filter(grupo => grupoTemItensAtivos(grupo)).map((grupo) => (
                <div key={`grupo_${grupo.id}`} style={{ marginBottom: '24px' }} className={shakeGroup === grupo.id ? 'shake' : ''}>
              <div style={{
                background: 'rgb(245, 246, 248)',
                border: '1px solid rgb(229, 231, 235)',
                borderRadius: '0px',
                padding: '22px',
                marginBottom: '12px',
                lineHeight: '11px'
              }}>
                <h5 style={{ 
                  fontWeight: '600', 
                  marginBottom: '6px',
                  fontSize: '16px',
                  color: '#1a1a1a'
                }}>
                  {grupo.nome}{grupo.b_obrigatorio ? ' (Obrigatório)' : ''}
                </h5>
                    {grupo.descricao && (
                      <p style={{ 
                        color: '#666', 
                        fontSize: '15px', 
                        marginBottom: '8px' 
                      }}>
                        {grupo.descricao}
                      </p>
                    )}
                    {(grupo.min > 0 || grupo.max) && (
                      <p style={{ 
                        color: '#888', 
                        fontSize: '13px', 
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}>
                        {grupo.min > 0 && `Mín: ${grupo.min} `}
                        {grupo.max && `Máx: ${grupo.max}`}
                        {!grupo.max && grupo.min > 0 && 'Máx: Ilimitado'}
                      </p>
                    )}
                    <div style={{
                      fontSize: '13px',
                      color: '#28a745',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {getGrupoTotal(grupo.id)}/{grupo.max || '∞'} {grupo.max === 1 ? '✓' : ''}
                      {(grupo.max === 1 && grupo.b_obrigatorio && getGrupoTotal(grupo.id) >= (grupo.min || 1)) && (
                        <span className="success-badge">Obrigatório selecionado ✓</span>
                      )}
                      {(grupo.max && grupo.max !== 1 && getGrupoTotal(grupo.id) >= grupo.max) && (
                        <span className="limit-badge" aria-label="Máximo atingido">✓</span>
                      )}
                    </div>
                  </div>
                  <div style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
                    {grupo.grupoOpcionais && grupo.grupoOpcionais.map((opcional, idx) => {
                      const opcionalDesabilitado = opcional.b_ativo === 0 || opcional.b_ativo === '0' || opcional.b_ativo === false;
                      return (
                      <div 
                        key={`opcional_${grupo.id}_${opcional.id}`} 
                        onClick={() => !modoVisualizacao && !opcionalDesabilitado && grupo.max === 1 && selecionarOpcional(grupo.id, opcional.id)}
                        className={(shakeItem && shakeItem.groupId === grupo.id && shakeItem.opcionalId === opcional.id) ? 'shake' : ''}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '16px',
                          borderBottom: idx !== grupo.grupoOpcionais.length-1 ? '1px solid #e5e7eb' : 'none',
                          cursor: !modoVisualizacao && !opcionalDesabilitado ? (grupo.max === 1 ? 'pointer' : 'default') : 'default',
                          opacity: opcionalDesabilitado ? 0.5 : 1,
                          backgroundColor: opcionalDesabilitado ? '#f9fafb' : 'transparent'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          flex: 1
                        }}>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: grupo.max === 1 ? '50%' : '4px',
                            border: '2px solid #e5e7eb',
                            marginRight: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: opcionalDesabilitado ? '#e5e7eb' : (isOpcionalSelecionado(grupo.id, opcional.id) ? '#006affff' : 'white'),
                            borderColor: opcionalDesabilitado ? '#e5e7eb' : (isOpcionalSelecionado(grupo.id, opcional.id) ? '#006affff' : '#e5e7eb'),
                            pointerEvents: 'none',
                            flexShrink: 0
                          }}>
                            {!opcionalDesabilitado && isOpcionalSelecionado(grupo.id, opcional.id) && (
                              <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                            )}
                          </div>
                          <div
                            style={{
                              width: '48px',
                              height: '48px',
                              marginRight: '12px',
                              flexShrink: 0,
                              backgroundImage: opcional.imagem 
                                ? `url(https://grubupload.s3.amazonaws.com/${opcional.imagem})` 
                                : 'url(https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg)',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center center',
                              borderRadius: '8px',
                              filter: opcionalDesabilitado ? 'grayscale(100%)' : 'none',
                              pointerEvents: 'none'
                            }}
                          />
                          <div style={{ pointerEvents: 'none' }}>
                            <div style={{ 
                              fontWeight: '500',
                              fontSize: '0.85rem',
                              color: opcionalDesabilitado ? '#9ca3af' : 'rgb(17, 24, 39)',
                              marginBottom: '2px',
                            }}>
                              {opcional.nome}
                             
                            </div>
                            <div style={{ 
                              color: opcionalDesabilitado ? '#9ca3af' : ((grupo.b_adicional === 1 && opcional.valor > 0) ? 'rgb(56, 170, 78)' : '#6b7280'), 
                              fontWeight: 700,
                              fontSize: '0.8rem'
                            }}>
                              {grupo.b_adicional === 0 ? 'Sem custo extra' : (opcional.valor > 0 ? `+ ${formatPrice(opcional.valor)}` : 'Item sem custo extra')}
                            </div>
                          </div>
                        </div>
                        {(!modoVisualizacao && !opcionalDesabilitado && !(grupo.max === 1)) && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            border: '1px solid #e5e7eb', 
                            borderRadius: '20px',
                            backgroundColor: '#fff'
                          }}>
                            <Button 
                              variant="light"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); decrementarOpcional(grupo.id, opcional.id); }}
                              style={{ 
                                border: 'none', 
                                borderRadius: '50%', 
                                width: '30px', 
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                backgroundColor: 'transparent',
                                color: '#666'
                              }}
                            >
                              −
                            </Button>
                            <span style={{ 
                              margin: '0 12px', 
                              minWidth: '20px', 
                              textAlign: 'center',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}>
                              {(gruposQuantidades[grupo.id]?.[opcional.id] || 0)}
                            </span>
                            <Button 
                              variant="light"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); incrementarOpcional(grupo, opcional.id); }}
                              style={{ 
                                border: 'none', 
                                borderRadius: '50%', 
                                width: '30px', 
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                backgroundColor: 'transparent',
                                color: '#666'
                              }}
                            >
                              +
                            </Button>
                          </div>
                        )}
                      </div>
                    )})}
                  </div>
                </div>
              ))}

              {/* Campo de Comentário Desktop */}
              {!modoVisualizacao && (
                <div style={{ marginTop: '24px' }}>
                  <label style={{ 
                    fontWeight: '600', 
                    marginBottom: '8px',
                    fontSize: '16px',
                    color: '#1a1a1a',
                    display: 'block'
                  }}>
                    Alguma observação?
                  </label>
                  <textarea
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    placeholder="Ex: Sem cebola, ponto da carne, etc."
                    maxLength={200}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      minHeight: '80px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#9ca3af', 
                    marginTop: '4px',
                    textAlign: 'right'
                  }}>
                    {comentario.length}/200
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
      )}
      
      {!modoVisualizacao ? (
        <div style={{ 
          position: isMobile ? 'fixed' : 'sticky', 
          bottom: 0,
          left: isMobile ? 0 : 'auto',
          right: isMobile ? 0 : 'auto',
          backgroundColor: 'white', 
          padding: isMobile ? '16px 20px' : '20px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          gap: '12px',
          zIndex: isMobile ? 1060 : 'auto',
          boxShadow: isMobile ? '0 -4px 12px rgba(0,0,0,0.1)' : 'none'
        }}>
          <Button 
            variant="outline-secondary" 
            onClick={onHide}
            className="cancel-btn"
            style={{ 
              flex: 1, 
              padding: '12px', 
              borderRadius: '12px',
              fontSize: '0.9rem',
              border: '1px solid #e5e7eb',
              color: '#6b7280'
            }}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={adicionarAoCarrinho}
            disabled={!validarSelecoes() || ((isDeliveryRoute || isBalcaoRoute || isMesaRoute) && !lojaAberta) || adicionando}
            style={{ 
              flex: 2, 
              padding: '12px', 
              borderRadius: '12px',
              backgroundColor: (validarSelecoes() && lojaAberta && !adicionando) ? '#0039ff' : '#ccc',
              border: 'none',
              fontSize: '0.9rem',
              fontWeight: '600',
              boxShadow: (validarSelecoes() && lojaAberta && !adicionando) ? '0 4px 12px rgba(0,106,255,0.25)' : 'none',
              opacity: (validarSelecoes() && lojaAberta && !adicionando) ? 1 : 0.6
            }}
          >
            {adicionando ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Adicionando...
              </span>
            ) : ((isDeliveryRoute || isBalcaoRoute || isMesaRoute) && !lojaAberta) ? 'Loja Fechada' : `Adicionar • ${formatPrice(calcularValorTotal())}`}
          </Button>
        </div>
      ) : (
        <div style={{ 
          position: isMobile ? 'fixed' : 'sticky', 
          bottom: 0,
          left: isMobile ? 0 : 'auto',
          right: isMobile ? 0 : 'auto', 
          backgroundColor: 'white', 
          padding: isMobile ? '16px 20px' : '20px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'center',
          zIndex: isMobile ? 1060 : 'auto',
          boxShadow: isMobile ? '0 -4px 12px rgba(0,0,0,0.1)' : 'none'
        }}>
          <Button 
            variant="outline-secondary" 
            onClick={onHide}
            className="cancel-btn"
            style={{ 
              padding: '12px 24px', 
              borderRadius: '8px',
              fontSize: '14px',
              border: '1px solid #ddd',
              color: '#666'
            }}
          >
            Fechar
          </Button>
        </div>
      )}
      </Modal>
    </>
  );
}

export default ProdutoModal;
